import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const TEMPLATES = {
  'text-to-game': [
    { id: 'endless-runner', label: 'Endless Runner', icon: 'Zap', desc: 'Infinite side-scrolling runner with obstacles', prompt: 'An endless runner game where a character runs through a neon city dodging obstacles and collecting coins. Speed increases over time.' },
    { id: 'puzzle-game', label: 'Puzzle Game', icon: 'Grid3X3', desc: 'Logic-based tile puzzle challenge', prompt: 'A puzzle game with colorful tiles that must be matched in groups of 3 or more. Include combo chains and level progression.' },
    { id: 'platformer', label: 'Platformer', icon: 'ArrowUp', desc: 'Classic jump-and-run adventure', prompt: 'A classic platformer with a hero jumping across floating platforms, collecting stars, and avoiding enemies. 3 levels with increasing difficulty.' },
  ],
  'prompt-to-mini-game': [
    { id: 'tap-game', label: 'Tap Frenzy', icon: 'MousePointer', desc: 'Fast-paced tap reaction game', prompt: 'A quick tap game where colorful targets appear and disappear. Tap them before they vanish. Combo multiplier for consecutive hits.' },
    { id: 'memory-match', label: 'Memory Match', icon: 'Brain', desc: 'Card flip memory challenge', prompt: 'A memory card matching game with cute animal characters. 4x4 grid of face-down cards. Match all pairs to win.' },
    { id: 'dodge-game', label: 'Dodge & Survive', icon: 'Shield', desc: 'Avoid incoming projectiles', prompt: 'A dodge game where the player avoids falling objects. Objects increase in speed and frequency. Survive as long as possible.' },
  ],
  'text-to-animation': [
    { id: 'character-intro', label: 'Character Intro', icon: 'User', desc: 'Hero character reveal animation', prompt: 'A dramatic character intro animation: hero walks into frame from darkness, lightning flashes, name appears with particle effects.' },
    { id: 'logo-animation', label: 'Logo Animation', icon: 'Star', desc: 'Animated logo reveal', prompt: 'A sleek logo animation where letters fly in from different directions, assemble, then glow with a pulse effect.' },
    { id: 'scene-transition', label: 'Scene Transition', icon: 'Layers', desc: 'Cinematic scene wipe effect', prompt: 'A cinematic scene transition with a portal opening effect, swirling particles, and a dramatic camera push through.' },
  ],
  'script-to-video': [
    { id: 'yt-intro', label: 'YouTube Intro', icon: 'Youtube', desc: '15-second channel intro', prompt: 'YouTube channel intro: Logo zooms in with energy burst, channel name appears with glitch effect, subscribe button animates in.' },
    { id: 'tiktok-short', label: 'TikTok Short', icon: 'Video', desc: 'Vertical short-form video', prompt: 'A fast-paced TikTok-style video with quick cuts, bold text overlays, trending transitions, and upbeat energy.' },
    { id: 'cinematic-trailer', label: 'Cinematic Trailer', icon: 'Film', desc: 'Epic game/movie trailer', prompt: 'Epic cinematic trailer: starts with silence, builds tension, reveals hero, climactic music swell, title card with dramatic reveal.' },
  ],
  'music-to-video': [
    { id: 'beat-visualizer', label: 'Beat Visualizer', icon: 'Activity', desc: 'Reactive audio waveform visual', prompt: 'A dynamic beat visualizer with pulsing waveforms, frequency bars, and particle explosions that react to every beat drop.' },
    { id: 'particle-viz', label: 'Particle Visualizer', icon: 'Sparkles', desc: 'Particle system music visual', prompt: 'A particle system visualizer where thousands of particles form shapes and patterns that morph and flow with the music.' },
    { id: 'lyric-video', label: 'Animated Lyrics', icon: 'AlignLeft', desc: 'Kinetic typography lyric video', prompt: 'An animated lyrics video with kinetic typography, each word appearing with unique animations synced to the vocal rhythm.' },
  ],
};

const MODE_COLORS = {
  'text-to-game': 'var(--color-primary)',
  'prompt-to-mini-game': 'var(--color-secondary)',
  'text-to-animation': 'var(--color-accent)',
  'script-to-video': '#10B981',
  'music-to-video': '#F43F5E',
};

export default function TemplateSystem({ mode, onSelectTemplate, disabled }) {
  const [expanded, setExpanded] = useState(false);
  const templates = TEMPLATES?.[mode] || [];
  const modeColor = MODE_COLORS?.[mode] || 'var(--color-primary)';

  if (!templates?.length) return null;

  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={() => setExpanded(v => !v)}
        className="flex items-center justify-between w-full px-3 py-2 rounded-xl border transition-all hover:opacity-80"
        style={{ borderColor: `${modeColor}40`, background: `${modeColor}08`, color: modeColor }}
      >
        <div className="flex items-center gap-2">
          <Icon name="LayoutTemplate" size={14} color={modeColor} />
          <span className="text-xs font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Quick Templates</span>
        </div>
        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} color={modeColor} />
      </button>

      {expanded && (
        <div className="flex flex-col gap-1.5 animate-fade-in-up">
          {templates?.map((tpl) => (
            <button
              key={tpl?.id}
              onClick={() => { onSelectTemplate?.(tpl?.prompt); setExpanded(false); }}
              disabled={disabled}
              className="flex items-center gap-3 p-2.5 rounded-xl border text-left transition-all hover:opacity-90 disabled:opacity-40"
              style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${modeColor}15` }}>
                <Icon name={tpl?.icon} size={15} color={modeColor} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{tpl?.label}</p>
                <p className="text-xs truncate" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{tpl?.desc}</p>
              </div>
              <Icon name="ArrowRight" size={12} color="var(--color-muted-foreground)" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
